import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api/admin';
import { ApiError } from '../api/client';

interface AdminLayoutProps {
  children: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['admin', 'me'],
    queryFn: adminApi.me,
    retry: false,
  });

  if (meQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-wc-gray">
        <p className="text-sm text-wc-muted">Loading…</p>
      </div>
    );
  }

  if (meQuery.isError) {
    const err = meQuery.error;
    if (err instanceof ApiError && err.status === 401) {
      navigate('/admin/login', { replace: true });
      return null;
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-wc-gray">
        <p className="text-sm text-wc-red">Failed to load admin session.</p>
      </div>
    );
  }

  const me = meQuery.data!;

  const handleLogout = async () => {
    try {
      await adminApi.logout();
    } finally {
      qc.removeQueries({ queryKey: ['admin'] });
      navigate('/admin/login', { replace: true });
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-wc-gray">
      <header className="border-b border-wc-muted/20 bg-wc-white">
        <div className="mx-auto flex max-w-portal items-center justify-between px-6 py-4">
          <Link to="/admin" className="flex items-center gap-3" aria-label="Weigandt admin">
            <img src="/weigandt-logo.svg" alt="Weigandt" className="h-7 w-auto" />
            <span className="text-sm font-semibold uppercase tracking-[0.18em] text-wc-blue">
              Admin
            </span>
          </Link>
          <div className="flex items-center gap-4 text-sm text-wc-muted-strong">
            <div className="hidden text-right sm:block">
              <div className="text-wc-black">{me.displayName ?? me.email}</div>
              <div className="text-xs text-wc-muted">{me.email}</div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-md border border-transparent px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-wc-red hover:border-wc-red"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-portal flex-1 px-6 py-8">{children}</main>
    </div>
  );
}
